// ────────────────────────────────────────────────────
// src/features/admin/components/AdminDashboard.tsx
// ────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Tabs from '../../../../shared/components/ui/Tabs';
import { useAuth } from '../../../../shared/auth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import OrchestratorManagement from '@/features/system/orchestrator/components/OrchestratorManagement';
import IAMManagement       from './IAMManagement';
import TableOpsAdmin       from './TableOpsAdmin';
import IoTManagement       from '@/features/system/devices/components/iot/IoTManagement';
import SharedLinksAdminPanel from '@/features/shared-scope/components/SharedLinksAdminPanel';
import SharedLinksActivityLog from '@/features/shared-scope/components/SharedLinksActivityLog';
import ShroomyStyleLab from '@/features/shared-scope/components/ShroomyStyleLab';
import ArchiverDashboard from '@/features/system/archiver/components/ArchiverDashboard';
import SchedulerManagement from './SchedulerManagement';
import ExpensesManagement from './ExpensesManagement';
import DbopsPanel from '@/features/system/dbops/components/DbopsPanel';
import '@/features/shared-scope/components/shroomy-neon.css';

export default function AdminDashboard() {
  const auth = useAuth();
  const { capabilities } = auth;

  const isSuperadmin = capabilities.isSuperadmin === true;
  const can = (cap: string) => isSuperadmin || capabilities[cap] === true;

  /* Build tab list based on capabilities (superadmin sees all) */
  const tabLabels = useMemo(() => {
    const labels: string[] = [];
    // IAM — combined User Management + RBAC Management + Memberships admin
    if (can('canManageUsers') || can('canManageRbac') || can('canManageIam')) labels.push('IAM');
    if (can('canManageOrchestrator')) labels.push('Orchestrator Management');
    // Table Ops — map editor + entity management
    if (can('canAdminTableOps')) labels.push('Table Ops');
    // Devices — IoT ESP32 sensor management
    if (can('canViewIotDevices')) labels.push('Devices');
    // Shared Links — forensic analytics for all shared scope URLs
    if (can('canViewIotDevices') || can('canAdminTableOps')) labels.push('Shared Links');
    // Archiver — Parquet archive pipeline management
    if (can('canManageOrchestrator')) labels.push('Archiver');
    // Scheduler — weekly calendar with event management
    if (can('canManageOrchestrator') || can('canAdminTableOps')) labels.push('Scheduler');
    // Expenses — personal/business expense tracker
    if (can('canManageOrchestrator') || can('canAdminTableOps')) labels.push('Expenses');
    // DB Ops — read-only snapshot of schema_migrations + seed ledger
    if (can('canManageOrchestrator')) labels.push('DB Ops');
    return labels;
  }, [capabilities]);

  const [activeTab, setActiveTab] = useState<string>(tabLabels[0] ?? '');
  const [sp] = useSearchParams();
  useDocumentTitle('Admin', activeTab || undefined);
  // Ensure activeTab stays valid if permissions load later
  React.useEffect(() => {
    if (!tabLabels.length) return;
    if (!activeTab || !tabLabels.includes(activeTab)) {
      setActiveTab(tabLabels[0]);
    }
  }, [tabLabels, activeTab]);

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('adminTab');
    if (v && tabLabels.includes(v) && v !== activeTab) setActiveTab(v);
  }, [sp]);

  /* ── 3) UI ─────────────────────────────────────────────────── */
  return (
    <div className="p-4 h-full min-h-0 flex flex-col gap-4">
      {/* Desktop-only header + tabs — BubbleNav handles mobile */}
      <div className={`hidden md:block space-y-2${activeTab === 'Shared Links' ? ' shroomy-neon-admin-header' : ''}`}>
        <h1 className="text-xl font-semibold tracking-tight">Admin Dashboard</h1>

        {tabLabels.length > 0 ? (
          <Tabs
            tabs={tabLabels}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            urlKey="adminTab"
          />
        ) : (
          <div className="text-sm text-neutral-400">
            You do not have access to any admin sections.
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0">
        {/* Orchestrator workers */}
        {activeTab === 'Orchestrator Management' && <OrchestratorManagement />}

        {/* IAM — Users + RBAC sub-tabs */}
        {activeTab === 'IAM' && <IAMManagement />}

        {/* Mercado Pago — account management + pipeline readiness */}
        {activeTab === 'Table Ops' && <TableOpsAdmin />}

        {/* Devices — IoT ESP32 sensor management */}
        {activeTab === 'Devices' && <IoTManagement />}

        {/* Shared Links — forensic analytics + activity log */}
        {activeTab === 'Shared Links' && <SharedLinksSection />}

        {/* Archiver — Parquet archive pipeline management */}
        {activeTab === 'Archiver' && <ArchiverDashboard />}

        {/* Scheduler — weekly calendar grid */}
        {activeTab === 'Scheduler' && <SchedulerManagement />}

        {/* Expenses — personal/business expense tracker */}
        {activeTab === 'Expenses' && <ExpensesManagement />}

        {/* DB Ops — schema_migrations head + seed ledger */}
        {activeTab === 'DB Ops' && <DbopsPanel />}

      </div>
    </div>
  );
}

// ── Shared Links sub-section with Generator + Activity Log ───────────────

const SL_TABS = ['Activity Log', 'Generator', 'Shroomy Style'] as const;

function SharedLinksSection() {
  const [slTab, setSlTab] = useState<string>('Activity Log');
  const [sp] = useSearchParams();

  useEffect(() => {
    const v = sp.get('slTab');
    if (v && (SL_TABS as readonly string[]).includes(v)) setSlTab(v);
  }, [sp]);

  return (
    <div className="shroomy-neon h-full min-h-0 flex flex-col gap-2">
      <Tabs
        tabs={[...SL_TABS]}
        activeTab={slTab}
        onTabChange={setSlTab}
        urlKey="slTab"
      />
      <div className="flex-1 min-h-0">
        {slTab === 'Activity Log' && <SharedLinksActivityLog />}
        {slTab === 'Generator'    && <SharedLinksAdminPanel />}
        {slTab === 'Shroomy Style' && <ShroomyStyleLab />}
      </div>
    </div>
  );
}
